import { Box, Flex, Inline, xcss } from '@atlaskit/primitives';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import {
  StreamingSkeletonItem,
  useDatabaseStreamState
} from '@databases/database-ai-first-creation-ui';
import { FieldTypeIcon } from '@databases/icons/FieldTypeIcon';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { SkeletonItem } from '@databases/skeleton';
import { DatabaseValueViewContainer } from '@databases/styles';
import { type DatabaseField, type DatabaseValue } from '@databases/types/database';
import { convertType } from '@databases/utils/database/field-types';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import React, { type FunctionComponent, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import styled from 'styled-components';
import { useSnapshot } from 'valtio';

import { getFieldType } from '../fields.tsx';
import { useLoadDependencies } from '../utils.ts';

const StyledSkeletonItem = styled(SkeletonItem)`
  &&& {
    display: block;
    min-width: 0;
  }
`;

const fieldNamePreviewContainerStyles = xcss({
  gap: 'space.100',
  color: 'color.text.subtlest',
  alignItems: 'center'
});

const fieldNamePreviewStyles = xcss({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  gap: 'space.100',
  color: 'color.text.subtlest'
});

const emptyStateContainerStyles = xcss({
  color: 'color.text.disabled'
});

export interface FieldValueViewProps {
  contentId: string;
  entryId?: string;
  field: DatabaseField;
  value: DatabaseValue;
  showPlaceholder?: boolean;
  noFocus?: boolean;
  recursionCount?: number;
  isReadonly?: boolean;
  /** If this is true, will show the field name as a placeholder for empty values. */
  showFieldNamePlaceholder?: boolean;
  /** If this is true, will show the new empty state */
  showFieldNameEmptyState?: boolean;
  fieldIds?: string[];
  showDisabledValue?: boolean;
  showEmptyValueLabel?: boolean;
  emptyValueLabel?: string;
  isEmbedValueView?: boolean;
}

/**
 * Renders the field as a database value based on the field type.
 */
export const FieldValueView: FunctionComponent<FieldValueViewProps> = ({
  contentId,
  entryId,
  field,
  value,
  showPlaceholder,
  noFocus,
  recursionCount = 0,
  isReadonly,
  showFieldNamePlaceholder,
  showFieldNameEmptyState,
  showDisabledValue,
  showEmptyValueLabel,
  emptyValueLabel,
  isEmbedValueView
}) => {
  const { formatMessage } = useIntl();
  const fieldType = getFieldType(field.type);
  const Component = fieldType.view;
  const store = defaultStoreUtils.useStore();
  // Not using useSelector() here, as some conversions generate random IDs, meaning that the converted value changes each time.
  // This way we only run the conversion when the original value changes.
  const convertedValue = useMemo(() => {
    return convertType({ field, value }, field.type, store.getState());
  }, [field, store, value]);

  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
  const { isStreaming, operationType } = useDatabaseStreamState();

  const {
    entryIdSelectedForProcessing,
    processedValues,
    isSingleFieldStreaming,
    fieldIdSelectedForProcessing
  } = useSnapshot(aiFirstCreationStreamingState);

  const fieldIdForProcessingRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    fieldIdForProcessingRef.current = fieldIdSelectedForProcessing;
  }, [fieldIdSelectedForProcessing]);

  const hasBeenProcessed = !!entryId && processedValues.has(`${field.id}-${entryId}`);
  const isCurrentEntryBeingProcessed = entryId === entryIdSelectedForProcessing;
  const isCurrentFieldBeingProcessed = field.id === fieldIdForProcessingRef.current;
  const renderStreamingPlaceholder =
    isCurrentEntryBeingProcessed && operationType === 'create';

  const renderStreamingPlaceholderForFieldValue =
    operationType === 'edit' &&
    Boolean(isSingleFieldStreaming) &&
    isCurrentFieldBeingProcessed &&
    !hasBeenProcessed;

  useLoadDependencies({ contentId, entryId, field, convertedValue }, store.getState());
  if (showEmptyValueLabel && emptyValueLabel) {
    return (
      <DatabaseValueViewContainer data-testid="database-value-view-container">
        <Box xcss={emptyStateContainerStyles}>{emptyValueLabel}</Box>
      </DatabaseValueViewContainer>
    );
  }

  if (showFieldNamePlaceholder) {
    // case where we want to display the empty state when field value is empty
    if (showFieldNameEmptyState) {
      return (
        <Box xcss={emptyStateContainerStyles}>
          <FormattedMessage
            {...messages['field-value.empty-state.default-text']}
            values={{
              defaultText: formatMessage(fieldType.defaultText)
            }}
          />
        </Box>
      );
    }

    return (
      <Inline xcss={fieldNamePreviewContainerStyles}>
        <FieldTypeIcon
          type={field.type}
          label={fieldType.label}
          noTooltip
          jiraField={field.settings.jiraField}
        />
        <Box xcss={fieldNamePreviewStyles}>{field.name}</Box>
      </Inline>
    );
  }

  if (
    isStreaming &&
    isDBAiFirstCreationEnabled &&
    (renderStreamingPlaceholder || renderStreamingPlaceholderForFieldValue)
  ) {
    return (
      <Flex alignItems="center" justifyContent="center">
        <StreamingSkeletonItem />
      </Flex>
    );
  }

  return (
    <DatabaseValueViewContainer data-testid="database-value-view-container">
      <StyledSkeletonItem data-testid="database-value-view-skeleton-item" minWidth={80}>
        <Component
          contentId={contentId}
          entryId={entryId}
          field={field}
          value={convertedValue.value}
          showPlaceholder={showPlaceholder}
          noFocus={noFocus}
          recursionCount={recursionCount}
          isReadonly={isReadonly}
          showDisabledValue={showDisabledValue}
          isEmbedValueView={isEmbedValueView}
        />
      </StyledSkeletonItem>
    </DatabaseValueViewContainer>
  );
};
